/**
 * Plan limits as the titlebar shows them (owner request 15.09.2026): one row
 * per limit with how much is used, so a Free account sees where it stands
 * before and after it reaches a limit.
 *
 * Input is `GET /auth/usage` (kept on `user._usage` by authStore.fetchUsage):
 *   limits.personal_boards / personal_documents / personal_sync_bytes /
 *   file_storage_bytes  (null = unlimited)
 *   usage.personal_sync { boards, documents, bytes }, usage.file_storage { bytes }
 */
import { formatBytes } from './planQuota';

/** At or above this share of a limit the titlebar cloud turns yellow. */
export const NEAR_LIMIT_SHARE = 0.8;

const ROWS = [
  { key: 'boards', limitKey: 'personal_boards', used: (u) => u?.personal_sync?.boards, bytes: false },
  { key: 'documents', limitKey: 'personal_documents', used: (u) => u?.personal_sync?.documents, bytes: false },
  { key: 'content', limitKey: 'personal_sync_bytes', used: (u) => u?.personal_sync?.bytes, bytes: true },
  { key: 'files', limitKey: 'file_storage_bytes', used: (u) => u?.file_storage?.bytes, bytes: true },
];

/**
 * `[{ key, used, limit, share, state, usedLabel, limitLabel }]` for the limits
 * this plan actually has (unlimited ones are left out). `state` is
 * 'ok' | 'near' | 'full'.
 */
export function limitRows(usage) {
  const limits = usage?.limits;
  if (!limits) return [];
  return ROWS.flatMap((row) => {
    const limit = limits[row.limitKey];
    if (typeof limit !== 'number' || limit <= 0) return [];
    const used = Math.max(0, Number(row.used(usage?.usage)) || 0);
    const share = limit === 0 ? 1 : Math.min(used / limit, 1);
    const state = used >= limit ? 'full' : share >= NEAR_LIMIT_SHARE ? 'near' : 'ok';
    return [{
      key: row.key,
      used,
      limit,
      share,
      state,
      usedLabel: row.bytes ? formatBytes(used) : String(used),
      limitLabel: row.bytes ? formatBytes(limit) : String(limit),
    }];
  });
}

/** 'ok' | 'near' | 'full' — the worst row. */
export function limitsStatus(usage) {
  const rows = limitRows(usage);
  if (rows.some((r) => r.state === 'full')) return 'full';
  if (rows.some((r) => r.state === 'near')) return 'near';
  return 'ok';
}
